import { Box, Flex, Text, Tooltip } from "@chakra-ui/react";
import { DatePicker, Typography, DateRangeSelector } from "@reconvert/react-ui-component";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import moment from "moment";
import { ConditionFieldErrors } from "../../../../utils/validateConditionData";
import FieldError from "../FieldError";

interface DateSelectorProps {
  conditionData: any;
  updateConditionData: (val: { value?: number; fromValue?: number; toValue?: number }) => void;
  showTooltip?: boolean;
  tooltipText?: string;
  label?: string;
  fieldErrors?: ConditionFieldErrors;
}

const DateSelector = ({
  conditionData,
  updateConditionData,
  showTooltip = false,
  tooltipText,
  label,
  fieldErrors,
}: DateSelectorProps) => {
  const { t } = useTranslation();

  const displayLabel = label || t("Value");

  const validationErrors = useMemo(() => {
    const errors = {
      fromValue: "",
      toValue: "",
      value: "",
    };

    if (fieldErrors?.fromValue) {
      errors.fromValue = fieldErrors?.fromValue;
    }

    if (fieldErrors?.toValue) {
      errors.toValue = fieldErrors?.toValue;
    }

    if (fieldErrors?.value) {
      errors.value = fieldErrors?.value;
    }

    if (conditionData?.condition === "between" || conditionData?.condition === "notBetween") {
      if (
        conditionData?.fromValue &&
        conditionData?.toValue &&
        Number(conditionData?.toValue) < Number(conditionData?.fromValue)
      ) {
        errors.toValue = t("To date must be greater than or equal to from date");
      }
    }

    return errors;
  }, [conditionData, t, fieldErrors]);

  const renderDatePicker = (
    value: string | undefined,
    onChange: (val: number) => void,
    error: string,
    withTooltip: boolean = true,
    fieldId: string
  ) => {
    const datePickerElement = (
      <Box>
        <DatePicker
          id={fieldId}
          size="sm"
          value={value ? moment(value).toDate() : undefined}
          defaultDate={value ? moment(value).toDate() : undefined}
          onDateValueChange={(date: Date) => {
            onChange(moment.utc(moment(date).format("YYYY-MM-DD")).valueOf());
          }}
          isInvalid={!!error}
          error={error}
          placement="autoVertical"
        />
      </Box>
    );

    if (showTooltip && withTooltip) {
      return (
        <Tooltip label={tooltipText} fontSize="xs" placement="top" hasArrow>
          {datePickerElement}
        </Tooltip>
      );
    }

    return datePickerElement;
  };

  if (conditionData?.condition === "between" || conditionData?.condition === "notBetween") {
    const dateRangeElement = (
      <Box mt={2}>
        <Typography color={"#6A7381"} width={"max-content"} bg={"var(--Natural-100, #F9FAFC)"} variant="small">
          {displayLabel}
        </Typography>
        <Box mt={2}>
          <DateRangeSelector
            startDate={conditionData?.fromValue ? moment(conditionData?.fromValue).toDate() : undefined}
            endDate={conditionData?.toValue ? moment(conditionData?.toValue).toDate() : undefined}
            onStartDateChange={(date: Date | undefined) => {
              if (date) {
                updateConditionData({ fromValue: moment.utc(moment(date).format("YYYY-MM-DD")).valueOf() });
              }
            }}
            onEndDateChange={(date: Date | undefined) => {
              if (date) {
                updateConditionData({ toValue: moment.utc(moment(date).format("YYYY-MM-DD")).valueOf() });
              }
            }}
            size="sm"
            separatorText={t("and")}
            width="100%"
            buttonWidth="140px"
            leftPlacement="topStart"
            rightPlacement="topEnd"
            isStartDateInvalid={!!validationErrors.fromValue}
            isEndDateInvalid={!!validationErrors.toValue}
            startDateError={validationErrors.fromValue}
            endDateError={validationErrors.toValue}
          />
        </Box>
      </Box>
    );

    if (showTooltip) {
      return (
        <Tooltip label={tooltipText} fontSize="xs" placement="top" hasArrow>
          {dateRangeElement}
        </Tooltip>
      );
    }

    return dateRangeElement;
  } else {
    return (
      <Box mt={2}>
        <Flex gap={1} flexDirection={"column"}>
          <Typography color={"#6A7381"} width={"max-content"} bg={"var(--Natural-100, #F9FAFC)"} variant="small">
            {displayLabel}
          </Typography>
          <Box>
            {renderDatePicker(
              conditionData?.value,
              val => updateConditionData({ value: val }),
              validationErrors.value,
              true,
              "value-date-picker"
            )}
          </Box>
        </Flex>
      </Box>
    );
  }
};

export default DateSelector;
